'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var format = require('string-format');
var DropdownButton = require('react-bootstrap/DropdownButton');
var Dropdown = require('react-bootstrap/Dropdown');
var HttpClient = require('@tunz/http');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var format__default = /*#__PURE__*/_interopDefaultLegacy(format);
var DropdownButton__default = /*#__PURE__*/_interopDefaultLegacy(DropdownButton);
var Dropdown__default = /*#__PURE__*/_interopDefaultLegacy(Dropdown);
var HttpClient__default = /*#__PURE__*/_interopDefaultLegacy(HttpClient);

const initialValue = {
    getTranslation: () => null,
    availableLanguages: [],
    currentLanguage: 'no-language',
    changeLanguage: () => undefined,
};
var TranslationContext = React__default["default"].createContext(initialValue);

/**
 * The translation features uses the React Context API.
 * You should surround your application in a TranslationProvider.
 * Inside this use the Translation component to translate a particular code
 */
const Translation = ({ children, defaultValue, formatValues }) => {
    const fallback = defaultValue || children;
    return (React__default["default"].createElement(TranslationContext.Consumer, null, ({ getTranslation }) => {
        const template = getTranslation(children) || fallback;
        return formatValues ? format__default["default"](template, formatValues) : template;
    }));
};
Translation.defaultProps = {
    defaultValue: undefined,
    formatValues: undefined,
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class TranslationProvider extends React__default["default"].Component {
    constructor() {
        super(...arguments);
        this.state = {
            translations: {},
            isLoading: true,
            availableLanguages: [],
            currentLanguage: this.props.lang || 'en-US',
        };
        this.componentDidMount = () => {
            this.loadLanguages().then(this.selectDefaultLanguage).then(this.loadTranslations);
        };
        this.getTranslation = (trCode, reportIfMissing = true) => {
            const { manager } = this.props;
            return manager.getTranslation(trCode, reportIfMissing);
        };
        /**
         * Find an available language that starts with langCode
         * @param langCode
         */
        this.getAvailableLangStartingWith = (langCode) => this.state.availableLanguages.find((l) => l.startsWith(langCode));
        this.loadLanguages = () => {
            this.setLoading();
            const { manager } = this.props;
            return manager.loadAvailableLanguages().then(languages => {
                this.setState(oldState => (Object.assign(Object.assign({}, oldState), { availableLanguages: languages })));
                return Promise.resolve(languages);
            });
        };
        this.loadTranslations = () => __awaiter(this, void 0, void 0, function* () {
            this.setLoading();
            const { manager } = this.props;
            const { currentLanguage } = this.state;
            const translations = yield manager.loadTranslations(currentLanguage);
            this.setState(oldState => (Object.assign(Object.assign({}, oldState), { translations, isLoading: false })));
        });
        /**
         * Change the language in the Provider.
         *
         * If the *Lang* prop is not set (=== null), the component is considered uncontrolled
         * (https://reactjs.org/docs/uncontrolled-components.html)
         * In that case, the component is in charge of changing
         * its own internal `currentLanguage` value.
         *
         * If the *Lang* prop is set (!== null), the component is considered controlled
         * (https://reactjs.org/docs/forms.html#controlled-components)
         * In that case, the component's parent is in charge of setting its language value and
         * the component should respect it not mather what.
         * The component's parent can pass a onLanguageChange callback to react accordingly.
         *
         * Note that the onLanguageChange is called anyway because if the parent passed a callback,
         * it means it would like to be notified whatever being controlled or uncontrolled.
         *
         * @param newLanguage
         */
        this.changeLanguage = (newLanguage) => {
            const { lang, onLanguageChange } = this.props;
            if (lang === TranslationProvider.defaultProps.lang) {
                this.setState(oldState => (Object.assign(Object.assign({}, oldState), { currentLanguage: newLanguage, isLoading: true })), this.loadTranslations);
            }
            else {
                // eslint-disable-next-line no-console
                console.warn('This TranslationProvider is controlled by its props. ' +
                    'Changing the language will have no effect unless you catch the onLanguageChange ' +
                    'and handle the change outside');
            }
            if (onLanguageChange) {
                onLanguageChange(newLanguage);
            }
        };
        /**
         * Detect the default language to apply
         */
        this.selectDefaultLanguage = () => {
            let language = this.props.lang;
            // Check if browser language has a full match
            const browserLang = navigator.language;
            if (language === undefined) {
                language = this.getAvailableLangStartingWith(browserLang);
            }
            // Check if browser language has a partial match
            if (language === undefined && browserLang.length === 5) {
                // xx-YY format
                const browserGenericLang = browserLang.substr(0, 2);
                language = this.getAvailableLangStartingWith(browserGenericLang);
            }
            if (language === undefined) {
                language = 'en-US';
            }
            const currentLanguage = language;
            this.setState(oldState => (Object.assign(Object.assign({}, oldState), { currentLanguage })));
            return Promise.resolve();
        };
        this.render = () => {
            const { loader, children } = this.props;
            const { isLoading, availableLanguages, currentLanguage } = this.state;
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            const contextValue = {
                availableLanguages,
                currentLanguage,
                getTranslation: this.getTranslation,
                changeLanguage: this.changeLanguage,
            };
            return isLoading || !currentLanguage ? (loader) : (React__default["default"].createElement(TranslationContext.Provider, { value: contextValue }, children));
        };
    }
    componentDidUpdate(prevProps) {
        const { lang } = this.props;
        if (prevProps.lang !== lang) {
            this.loadTranslations();
        }
    }
    setLoading(loading = true) {
        this.setState(oldState => (Object.assign(Object.assign({}, oldState), { isLoading: loading })));
    }
}
TranslationProvider.defaultProps = {
    lang: 'en-US',
    onLanguageChange: () => undefined,
};

const TranslationSelector = ({ variant = 'secondary' }) => (React__default["default"].createElement(TranslationContext.Consumer, null, ({ currentLanguage, availableLanguages, getTranslation, changeLanguage }) => (React__default["default"].createElement("div", null,
    React__default["default"].createElement(DropdownButton__default["default"], { variant: variant, title: getTranslation(currentLanguage) || currentLanguage, id: "TranslationSelector", onSelect: (newLang) => newLang && changeLanguage(newLang), "data-testid": "language-selection-button" }, availableLanguages.map(l => (React__default["default"].createElement(Dropdown__default["default"].Item, { key: l, eventKey: l, className: l }, getTranslation(l) || l))))))));
TranslationSelector.defaultProps = {
    variant: undefined,
};

const DEFAULT_LANG = 'en-US';
class TranslationServerManager {
    constructor(application, url, version) {
        this.MAX_MISSING_CODES = 5;
        this.missingCodesByLang = new Map();
        this.sentCodesByLang = new Map();
        this.busy = false;
        this.currentLanguage = DEFAULT_LANG;
        this.availableLanguages = [];
        this.translations = {};
        this.loadTranslations = (language) => __awaiter(this, void 0, void 0, function* () {
            if (this.currentLanguage === undefined) {
                this.setLanguage(language);
            }
            const lang = language || this.currentLanguage;
            const response = yield HttpClient__default["default"].get(`${this.apiUrl}/${this.app}/${lang}?version=${this.version}`);
            const translations = {};
            response.data.forEach((d) => {
                translations[d.msgId] = d.content;
            });
            this.translations = translations;
            return this.translations;
        });
        this.loadAvailableLanguages = () => __awaiter(this, void 0, void 0, function* () {
            const response = yield HttpClient__default["default"].get(`${this.apiUrl}/${this.app}/languages`);
            this.availableLanguages = response.data.map((l) => `${l.language}-${l.country}`);
            return this.availableLanguages;
        });
        this.sendMissingTranslationsToServer = (missingCodes) => Promise.all(Array.from(missingCodes.entries()).map(([lang, codes]) => HttpClient__default["default"].post(`${this.apiUrl}/${this.app}/${lang}/missing`, JSON.stringify(Array.from(codes.values())), {
            headers: {
                'Content-Type': 'application/json',
            },
        })));
        this.app = application || 'backoffice';
        this.apiUrl = url || '/api/translations';
        this.version = version || 'PUBLISHED';
        this.loadAvailableLanguages().then(languages => {
            this.availableLanguages = languages;
        });
    }
    setLanguage(language) {
        if (language) {
            this.currentLanguage = language;
        }
    }
    getTranslation(trCode, reportIfMissing) {
        if (trCode === undefined || trCode === null) {
            return null;
        }
        const foundTranslation = this.translations[trCode] || null;
        if (foundTranslation === null && reportIfMissing) {
            this.reportMissingTranslation(this.currentLanguage, trCode);
        }
        return foundTranslation;
    }
    reportMissingTranslation(language, code) {
        const alreadySentCodes = this.sentCodesByLang.get(language) || new Set();
        if (!alreadySentCodes.has(code)) {
            const codes = this.missingCodesByLang.get(language) || new Set();
            codes.add(code);
            this.missingCodesByLang.set(language, codes);
            if (TranslationServerManager.size(this.missingCodesByLang) >= this.MAX_MISSING_CODES &&
                !this.busy) {
                this.busy = true;
                // Copy the codes to send, so we can clear the missingCodesByLang
                // to keep accepting new codes while sending
                const missingCodesToSend = new Map(this.missingCodesByLang);
                this.missingCodesByLang.clear();
                this.sendMissingTranslationsToServer(missingCodesToSend)
                    .then(() => {
                    this.busy = false;
                    this.addToSentCodes(missingCodesToSend);
                })
                    .catch(error => {
                    this.busy = false;
                    // eslint-disable-next-line no-console
                    console.error(error);
                });
            }
        }
    }
    addToSentCodes(missingCodesToSend) {
        missingCodesToSend.forEach((codes, lang) => {
            if (this.sentCodesByLang.get(lang) === undefined) {
                this.sentCodesByLang.set(lang, new Set());
            }
            codes.forEach(c => {
                const map = this.sentCodesByLang.get(lang);
                if (map) {
                    map.add(c);
                }
            });
        });
    }
    static size(missingCodes) {
        let nbCodes = 0;
        missingCodes.forEach(codes => {
            nbCodes += codes.size;
        });
        return nbCodes;
    }
}

const TranslationCondition = (props) => {
    const { code, children } = props;
    return (React__default["default"].createElement(TranslationContext.Consumer, null, ({ getTranslation }) => {
        const translatedText = getTranslation(code);
        return translatedText ? children(translatedText) : null;
    }));
};

class LocalTranslationManager {
    constructor(localTranslations) {
        this.localTranslations = localTranslations;
        this.currentLanguage = 'en-US';
    }
    getTranslation(trCode) {
        if (this.localTranslations[this.currentLanguage]) {
            return this.localTranslations[this.currentLanguage][trCode] || null;
        }
        return null;
    }
    loadAvailableLanguages() {
        return Promise.resolve(Object.keys(this.localTranslations));
    }
    loadTranslations(language) {
        if (language) {
            this.setLanguage(language);
        }
        return Promise.resolve(this.localTranslations[this.currentLanguage]);
    }
    setLanguage(language) {
        this.currentLanguage = language;
    }
}

exports.LocalTranslationManager = LocalTranslationManager;
exports.TranslationCondition = TranslationCondition;
exports.TranslationContext = TranslationContext;
exports.TranslationProvider = TranslationProvider;
exports.TranslationSelector = TranslationSelector;
exports.TranslationServerManager = TranslationServerManager;
exports["default"] = Translation;
